import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import logger from "../../art-logger-config";
import { getFromLocalStorage } from "../../util/localStorageUtils";
import { oneClickLaunchTableColumnDefinitions } from "./oneClickLaunchTable-config";
import { AppThunk } from "../app/store";
import {
  AsinMarketplaceLaunchAttributes,
  AsinMarketplaceLaunchAttributesList,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { GetOclKeyFromFullAsin } from "./oneClickLaunchConnector";
import {
  publishLaunchAttributes,
  removeWeblabFromAsinMarketplaces,
} from "../app/api";
import { ERROR_NOTIF_ITEMS } from "../notification/notification-config";
import {
  getCurrentEpochMilliseconds,
  getResetEpochMilliseconds,
  classifyAnnounceDate,
  AnnounceDateClassification,
} from "../../util/dateUtils";
import { AsinMarketplace } from "@amzn/coral_com-amazon-asincentral-common";
import {
  addNotification,
  clearNotifications,
  createNotificationDefinition,
} from "../notification/notificationConnector";

interface AsinOneClickLaunchTableControlState {
  preferences: {
    visibleContent: string[];
  };
  sort?: { sortingField: string };
  descending: boolean;
  oneClickLaunchInProgress: boolean;
  oneClickLaunchError: string | null;
  asinMarketplaces: Array<FullAsinWithAudits | RestrictedAsin>;
  selectedAsinMarketplaces: Array<FullAsinWithAudits | RestrictedAsin>;
  removedAsinMarketplacesKeys: string[];
  selectedAsinMarketplacesKeys: string[];
  isPublishingLaunchAttributes: boolean;
  publishLaunchAttributesError: boolean;
  removeWeblabFromAsinMarketplacesInProgress: boolean;
  removeWeblabFromAsinMarketplacesError: boolean;
  asinMarketplacesContainsRestrictedItems: boolean;
  asinMarketplacesContainsUnselectedItems: boolean;
  asinMarketplacesContainsSelectedItems: boolean;
  asinMarketplacesContainsOutOfBoundsDates: boolean;
  asinMarketplacesContainsPastDates: boolean;
  outOfBoundsReasons: ("future" | "missing")[];
  selectedAsinMarketplacesContainsRestrictedItems: boolean;
  selectedAsinMarketplacesContainsLiveItems: boolean;
}

function getInitialPreferences() {
  const defaultInitialPreferences = {
    visibleContent: oneClickLaunchTableColumnDefinitions.options.flatMap(
      (item) => item.options.filter((c) => c.defaultVisible).map((c) => c.id)
    ),
  };

  try {
    const localPrefsString = getFromLocalStorage(
      "ONE_CLICK_LAUNCH_TABLE_PREFERENCES"
    );
    if (localPrefsString !== null) {
      return JSON.parse(localPrefsString);
    }
  } catch (e: any) {
    logger.error(
      `Failed to load initial preferences for oneClickLaunch table: ${e.toString()}`
    );
  }
  return defaultInitialPreferences;
}

const asinOneClickLaunchTableControlInitialState: AsinOneClickLaunchTableControlState =
  {
    asinMarketplaces: [],
    selectedAsinMarketplaces: [],
    removedAsinMarketplacesKeys: [],
    selectedAsinMarketplacesKeys: [],
    preferences: getInitialPreferences(),
    descending: true,
    oneClickLaunchInProgress: false,
    oneClickLaunchError: null,
    isPublishingLaunchAttributes: false,
    publishLaunchAttributesError: false,
    removeWeblabFromAsinMarketplacesInProgress: false,
    removeWeblabFromAsinMarketplacesError: false,
    asinMarketplacesContainsRestrictedItems: false,
    asinMarketplacesContainsUnselectedItems: false,
    asinMarketplacesContainsSelectedItems: false,
    asinMarketplacesContainsOutOfBoundsDates: false,
    asinMarketplacesContainsPastDates: false,
    outOfBoundsReasons: [],
    selectedAsinMarketplacesContainsRestrictedItems: false,
    selectedAsinMarketplacesContainsLiveItems: false,
  };

export function getSelectedItemKey(item: FullAsinWithAudits | RestrictedAsin) {
  return `${(item as FullAsinWithAudits).asin}_${
    (item as FullAsinWithAudits).obfuscatedMarketplaceId
  }`;
}

function getItemAnnounceDateClassification(
  item: FullAsinWithAudits | RestrictedAsin
): AnnounceDateClassification {
  if (!item || item.isRestricted) {
    return "missing";
  }
  const announceDate = (item as FullAsinWithAudits).announceDate;
  if (!announceDate) {
    return "missing";
  }
  return classifyAnnounceDate(new Date(announceDate));
}

function asinMarketplacesContainsAnyNonSelectedItems(
  asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
  selectedAsinMarketplacesKeys: Set<string>
) {
  return asinMarketplaces.some(
    (item) => !selectedAsinMarketplacesKeys.has(getSelectedItemKey(item))
  );
}

function selectedAsinMarketplacesContainsLiveItems(
  selectedAsinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[]
) {
  return selectedAsinMarketplaces
    .filter((item) => !item.isRestricted)
    .some(
      (item) =>
        (item as FullAsinWithAudits).catalogLiveness === undefined ||
        (item as FullAsinWithAudits).catalogLiveness === "live"
    );
}

function asinMarketplacesContainsAnySelectedItems(
  selectedAsinMarketplacesKeys: string[]
) {
  return selectedAsinMarketplacesKeys.length > 0;
}

const asinOneClickLaunchTableControl = createSlice({
  name: "asinOneClickLaunchTableControl",
  initialState: asinOneClickLaunchTableControlInitialState,
  reducers: {
    setPreferences(state, action) {
      state.preferences.visibleContent = action.payload.visibleContent;
    },
    mergeVisibleContentPreferences(state, action) {
      const activeColumns = action.payload?.activeColumns ?? [];
      state.preferences.visibleContent = Array.from(
        new Set<string>([...state.preferences.visibleContent, ...activeColumns])
      );
    },
    setSort(state, action) {
      state.sort = action.payload.sortingColumn;
      state.descending = action.payload.isDescending;
    },
    setAsinMarketplaces(
      state,
      action: PayloadAction<(FullAsinWithAudits | RestrictedAsin)[]>
    ) {
      state.asinMarketplaces = action.payload;
      state.asinMarketplacesContainsRestrictedItems = action.payload.some(
        (item) => item.isRestricted
      );
      let containsOutOfBounds = false;
      let containsPast = false;
      const reasonsSet = new Set<"future" | "missing">();
      for (const item of action.payload) {
        const classification = getItemAnnounceDateClassification(item);
        if (classification === "future" || classification === "missing") {
          containsOutOfBounds = true;
          reasonsSet.add(classification);
        } else if (classification === "past") {
          containsPast = true;
        }
      }
      state.asinMarketplacesContainsOutOfBoundsDates = containsOutOfBounds;
      state.asinMarketplacesContainsPastDates = containsPast;
      state.outOfBoundsReasons = Array.from(reasonsSet);
      state.asinMarketplacesContainsUnselectedItems =
        asinMarketplacesContainsAnyNonSelectedItems(
          state.asinMarketplaces,
          new Set(state.selectedAsinMarketplacesKeys)
        );
      state.asinMarketplacesContainsSelectedItems =
        asinMarketplacesContainsAnySelectedItems(
          state.selectedAsinMarketplacesKeys
        );
      state.selectedAsinMarketplacesContainsLiveItems =
        selectedAsinMarketplacesContainsLiveItems(
          state.selectedAsinMarketplaces
        );
    },
    setSelectedAsinMarketplaces(
      state,
      action: PayloadAction<(FullAsinWithAudits | RestrictedAsin)[]>
    ) {
      state.selectedAsinMarketplaces = action.payload;
      state.selectedAsinMarketplacesKeys = action.payload.map((item) =>
        getSelectedItemKey(item)
      );
      state.asinMarketplacesContainsUnselectedItems =
        asinMarketplacesContainsAnyNonSelectedItems(
          state.asinMarketplaces,
          new Set(state.selectedAsinMarketplacesKeys)
        );
      state.asinMarketplacesContainsSelectedItems =
        asinMarketplacesContainsAnySelectedItems(
          state.selectedAsinMarketplacesKeys
        );
      state.selectedAsinMarketplacesContainsRestrictedItems =
        action.payload.some((item) => item.isRestricted);
      state.selectedAsinMarketplacesContainsLiveItems =
        selectedAsinMarketplacesContainsLiveItems(
          state.selectedAsinMarketplaces
        );
    },
    addRemovedAsinMarketplaces(state, action: PayloadAction<string[]>) {
      state.removedAsinMarketplacesKeys = [
        ...state.removedAsinMarketplacesKeys,
        ...action.payload,
      ];
    },
    clearRemovedAsinMarketplaces(state) {
      state.removedAsinMarketplacesKeys = [];
    },
    startPublishLaunchAttributes: (state) => {
      state.publishLaunchAttributesError = false;
      state.isPublishingLaunchAttributes = true;
    },
    endPublishLaunchAttributes: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.publishLaunchAttributesError = true;
      }
      state.isPublishingLaunchAttributes = false;
    },
    startRemoveWeblabFromAsinMarketplaces: (state) => {
      state.removeWeblabFromAsinMarketplacesError = false;
      state.removeWeblabFromAsinMarketplacesInProgress = true;
    },
    endRemoveWeblabFromAsinMarketplaces: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload) {
        state.removeWeblabFromAsinMarketplacesError = true;
      }
      state.removeWeblabFromAsinMarketplacesInProgress = false;
    },
  },
});

export const {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  setAsinMarketplaces,
  setSelectedAsinMarketplaces,
  addRemovedAsinMarketplaces,
  clearRemovedAsinMarketplaces,
  startPublishLaunchAttributes,
  endPublishLaunchAttributes,
  startRemoveWeblabFromAsinMarketplaces,
  endRemoveWeblabFromAsinMarketplaces,
} = asinOneClickLaunchTableControl.actions;

export default asinOneClickLaunchTableControl.reducer;

function extractAsinAndMarketplaceFromKey(key: string) {
  const items = key.split("_");
  if (items.length !== 2) {
    // Throw here?
  }
  return [items[0], items[1]];
}

export const resetLaunchDates =
  (
    asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
    asinMarketplaceKeys: string[]
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startPublishLaunchAttributes());
      dispatch(
        createNotificationDefinition(
          "info",
          "Resetting Launch Dates...",
          true,
          `Attempting to reset launch dates for ${asinMarketplaceKeys.length} ASIN Marketplaces.`
        )
      );
      const selectedSet = new Set(asinMarketplaceKeys);
      const filteredItems = asinMarketplaces.filter((item) =>
        selectedSet.has(getSelectedItemKey(item))
      );
      const asinMarketplacePartNumbers = new Map(
        filteredItems.map((item) => [
          getSelectedItemKey(item),
          !item.isRestricted ? item.partNumber : null,
        ])
      );
      const asinMarketplaceAsinTypes = new Map(
        filteredItems.map((item) => [
          getSelectedItemKey(item),
          !item.isRestricted ? (item as FullAsinWithAudits).asinType : null,
        ])
      );
      const resetDate = getResetEpochMilliseconds();
      const asinMps = asinMarketplaceKeys.map((key) => {
        const [asin, obfuscatedMarketplaceId] =
          extractAsinAndMarketplaceFromKey(key);
        return AsinMarketplaceLaunchAttributes.fromJson({
          asin: asin,
          partNumber: asinMarketplacePartNumbers.get(key),
          obfuscatedMarketplaceId: obfuscatedMarketplaceId,
          asinType: asinMarketplaceAsinTypes.get(key),
          productSiteEmbargoDate: resetDate,
          productSiteLaunchDate: resetDate,
        });
      });

      const result = await publishLaunchAttributes(
        new AsinMarketplaceLaunchAttributesList(asinMps)
      );
      dispatch(endPublishLaunchAttributes(false));
      dispatch(
        createNotificationDefinition(
          "success",
          "Reset Launch Dates Called",
          true,
          `Reset Launch Dates has been called for your ${asinMarketplaceKeys.length} ASIN Marketplaces successfully. Your workflow ID is ${result.workflowId}`
        )
      );
    } catch (err: any) {
      dispatch(clearNotifications());
      dispatch(addNotification(ERROR_NOTIF_ITEMS(err.toString())));
      dispatch(endPublishLaunchAttributes(true));
    }
  };

/*
All the asin-mps should be included in the request and the productSiteEmbargoDate & productSiteLaunchDate
should be set while the indexSuppressed and thirdPartySiteExcluded attributes should be null.
 */
export const publishLaunchDates =
  (asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startPublishLaunchAttributes());
      dispatch(
        createNotificationDefinition(
          "info",
          "Publishing Launch Attributes...",
          true,
          `Attempting to publish launch attributes for ${asinMarketplaces.length} ASIN marketplaces.`
        )
      );
      const currentDate = getCurrentEpochMilliseconds();
      const asinMps = asinMarketplaces.map((item) => {
        item = item as FullAsinWithAudits;
        return AsinMarketplaceLaunchAttributes.fromJson({
          asin: item.asin,
          partNumber: item.partNumber,
          obfuscatedMarketplaceId: item.obfuscatedMarketplaceId,
          asinType: item.asinType,
          indexSuppressed: null,
          thirdPartySiteExcluded: null,
          productSiteEmbargoDate: currentDate,
          productSiteLaunchDate: currentDate,
        });
      });

      const result = await publishLaunchAttributes(
        new AsinMarketplaceLaunchAttributesList(asinMps)
      );
      dispatch(
        createNotificationDefinition(
          "success",
          "Publishing Launch Attributes Called",
          true,
          `Publishing Launch Attributes has been called for your ${asinMarketplaces.length} ASIN Marketplaces successfully. Your workflow ID is ${result.workflowId}`
        )
      );
      dispatch(endPublishLaunchAttributes(false));
    } catch (err: any) {
      dispatch(addNotification(ERROR_NOTIF_ITEMS(err.toString())));
      dispatch(endPublishLaunchAttributes(true));
    }
  };

/*
All the asin-mps should be included in the request and their indexSuppressed and thirdPartySiteExcluded should be
set (to values provided by the user) while the productSiteEmbargoDate & productSiteLaunchDate attributes should be null.
 */
export const publishIndexSuppressedAnd3pse =
  (
    asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
    indexSuppressedSelectedMap: { [key: string]: boolean },
    _3pseSelectedMap: { [key: string]: boolean }
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startPublishLaunchAttributes());
      dispatch(
        createNotificationDefinition(
          "info",
          "Publishing Launch Attributes...",
          true,
          `Attempting to publish launch attributes for ${asinMarketplaces.length} ASIN marketplaces.`
        )
      );
      const asinMps = asinMarketplaces.map((item) => {
        item = item as FullAsinWithAudits;
        return AsinMarketplaceLaunchAttributes.fromJson({
          asin: item.asin,
          partNumber: item.partNumber,
          obfuscatedMarketplaceId: item.obfuscatedMarketplaceId,
          asinType: item.asinType,
          indexSuppressed:
            indexSuppressedSelectedMap[GetOclKeyFromFullAsin(item)] ?? false,
          thirdPartySiteExcluded:
            _3pseSelectedMap[GetOclKeyFromFullAsin(item)] ?? false,
          productSiteEmbargoDate: null,
          productSiteLaunchDate: null,
        });
      });

      const result = await publishLaunchAttributes(
        new AsinMarketplaceLaunchAttributesList(asinMps)
      );
      dispatch(endPublishLaunchAttributes(false));
      dispatch(
        createNotificationDefinition(
          "success",
          "Publishing Launch Attributes Called",
          true,
          `Publishing Launch Attributes has been called for your ${asinMarketplaces.length} ASIN Marketplaces successfully. Your workflow ID is ${result.workflowId}`
        )
      );
    } catch (err: any) {
      dispatch(addNotification(ERROR_NOTIF_ITEMS(err.toString())));
      dispatch(endPublishLaunchAttributes(true));
    }
  };

export const batchRemoveWeblabFromAsinMarketplaces =
  (asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startRemoveWeblabFromAsinMarketplaces());
      dispatch(
        createNotificationDefinition(
          "info",
          "Removing Weblab...",
          true,
          `Attempting to remove weblab for ${asinMarketplaces.length} ASIN marketplaces.`
        )
      );
      const asinMps = asinMarketplaces.map((item) => {
        item = item as FullAsinWithAudits;
        return AsinMarketplace.fromJson({
          asin: item.asin,
          obfuscatedMarketplaceId: item.obfuscatedMarketplaceId,
        });
      });

      const result = await removeWeblabFromAsinMarketplaces(asinMps);
      dispatch(endRemoveWeblabFromAsinMarketplaces(false));
      dispatch(
        createNotificationDefinition(
          "success",
          "Remove Weblab Called",
          true,
          `Remove Weblab has been called for your ${asinMarketplaces.length} ASIN Marketplaces successfully. Your workflow ID is ${result.workflowId}`
        )
      );
    } catch (err: any) {
      dispatch(addNotification(ERROR_NOTIF_ITEMS(err.toString())));
      dispatch(endRemoveWeblabFromAsinMarketplaces(true));
    }
  };
